import type { AppDispatch } from '../redux/Store';

// Employee (POS) slices
import { exitEditingCurrentOrder } from '../redux/cartActions';
import {
  clearCompletedOrders,
  clearPendingOrders,
  clearPartialOrders,
  clearFilteredCompletedOrder,
  clearFilteredPendingOrder,
  clearFilteredPartialOrder,
  clearTopCustomers,
} from '../redux/OrdersSlice';
import { clearCheckout } from '../redux/checkoutSlice';
import { clearPartialPaymentsDetails } from '../redux/partialPaymentsSlice';
import { addMpesaPayments } from '../redux/mpesaPaymentSlice';
import { resetProductSort } from '../redux/productSortSlice';
import {
  clearAllProducts,
  resetSearchProducts,
  clearCategories,
  clearSubCategories,
  clearProductsNames,
  clearProductsBySubCategory,
  clearSearchedProductsFromScanner,
} from '../redux/productSlice';

// Backoffice (owner) slices
import {
  clearCompleted as boClearCompleted,
  clearPending as boClearPending,
  clearPartial as boClearPartial,
  clearTableSearch as boClearTableSearch,
} from '../redux/backoffice/ordersSlice';
import { clearSummary } from '../redux/backoffice/summarySlice';
import {
  clearTableSearch as boProductsClearTableSearch,
  setProducts as boSetProducts,
} from '../redux/backoffice/productsSlice';
import {
  setCurrentAndLastMonth,
  setDailyPayments,
  setRevenueOverTime,
  setWeeklySalesData,
  setProductsReport,
} from '../redux/backoffice/reportsSlice';
import { clearReport } from '../redux/backoffice/reportsTableSlice';
import { clearInvoices } from '../redux/backoffice/billingSlice';
import { setEmployees } from '../redux/backoffice/employeesSlice';
import {
  setMonthlySales,
  setSalesOverTime,
  setCategorySales,
  setTransactions,
} from '../redux/backoffice/salesSlice';
import { setDashboardData } from '../redux/backoffice/dashboardSlice';
import {
  setMonthlyData,
  setAnnualPoints,
  setCategoryItems,
  setSummaryData,
  resetExpensesList,
  setDetailData,
} from '../redux/backoffice/expensesSlice';
import {
  setTargetAmount,
  setTargetError,
  setTargetLoading,
} from '../redux/backoffice/targetsSlice';

/**
 * State tied to the location just left, cleared after a location switch so
 * the new location never shows the old one's data.
 */
export function clearOwnerLocationState(dispatch: AppDispatch): void {
  try {
    // Backoffice orders
    dispatch(boClearCompleted());
    dispatch(boClearPending());
    dispatch(boClearPartial());
    dispatch(boClearTableSearch());

    // Backoffice products
    dispatch(boProductsClearTableSearch());
    dispatch(boSetProducts({ items: [], total: 0, page: 1, pageSize: 500 }));

    // Backoffice summary
    dispatch(clearSummary());

    // Backoffice reports
    dispatch(setDailyPayments([]));
    dispatch(setRevenueOverTime(null));
    dispatch(setWeeklySalesData([] as any));
    dispatch(
      setCurrentAndLastMonth({
        lastMonth: { totalSales: 0 },
        currentMonth: { totalSales: 0 },
      }),
    );
    dispatch(
      setProductsReport({
        docs: [],
        totalDocs: 0,
        totalPages: 0,
        page: 1,
        perPage: 0,
        metadata: {
          totalItemsSold: 0,
          totalAmountWasted: 0,
          totalItemsWasted: 0,
          totalUpdatedProducts: 0,
          totalUpdatedProductsAmount: 0,
          totalSalesMade: 0,
        },
      } as any),
    );

    // Backoffice reports table
    dispatch(clearReport());

    // Backoffice billing
    dispatch(clearInvoices());

    // Backoffice employees
    dispatch(setEmployees({ items: [], total: 0, page: 1, pageSize: 100 }));

    // Backoffice sales
    dispatch(setMonthlySales([]));
    dispatch(setSalesOverTime(null));
    dispatch(setCategorySales([]));
    dispatch(
      setTransactions({
        items: [],
        total: 0,
        page: 1,
        pageSize: 100,
        totalPages: 0,
        totalAmount: 0,
      }),
    );

    // Backoffice dashboard
    dispatch(
      setDashboardData({
        totalSales: 0,
        totalQuantitiesSold: 0,
        totalMassSold: 0,
        totalProfit: 0,
        totalOrders: 0,
        totalExpenses: 0,
      }),
    );

    // Backoffice expenses
    dispatch(setMonthlyData({ payments: 0, stocks: 0, others: 0 }));
    dispatch(setAnnualPoints([]));
    dispatch(setCategoryItems([]));
    dispatch(setSummaryData(null));
    dispatch(resetExpensesList());
    dispatch(setDetailData(null));

    // Backoffice targets
    dispatch(setTargetLoading(false));
    dispatch(setTargetError(null));
    dispatch(setTargetAmount(null));
  } catch (e) {
    console.warn('clearOwnerState error', e);
  }
}

export function clearEmployeeLocationState(dispatch: AppDispatch): void {
  try {
    // Orders (POS)
    dispatch(clearCompletedOrders());
    dispatch(clearPendingOrders());
    dispatch(clearPartialOrders());
    dispatch(clearFilteredCompletedOrder());
    dispatch(clearFilteredPendingOrder());
    dispatch(clearFilteredPartialOrder());
    dispatch(clearTopCustomers());

    // Cart & checkout. A saved order open in the basket is closed too, so
    // the next sale at the new location is not treated as an edit of it.
    dispatch(exitEditingCurrentOrder({ alsoClearCart: true }));
    dispatch(clearCheckout());
    dispatch(clearPartialPaymentsDetails());
    dispatch(addMpesaPayments([]));
    dispatch(resetProductSort());

    // Products (POS)
    dispatch(clearAllProducts());
    dispatch(resetSearchProducts());
    dispatch(clearCategories());
    dispatch(clearSubCategories());
    dispatch(clearProductsNames());
    dispatch(clearProductsBySubCategory());
    dispatch(clearSearchedProductsFromScanner());
  } catch (e) {
    console.warn('clearEmployeeState error', e);
  }
}
